import { Component, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { CProdProvisionFilters } from "@/data/filters/contractProducts";

// Types
import { IState } from "@/store";
import { IFilter } from "@/models/tables";

import {
  IBlueprint,
  IProvisionConfiguration,
  IProvisionCategory
} from "@/models/provisioning";
import { OrgConfigKeys } from "@/data/constants";

@Component({
  computed: {
    ...mapState({
      blueprintsScope(state: IState, getters) {
        return getters[`data/${this.blueprintsDataModule}/blueprintsScope`];
      },
      blueprints(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.blueprintsDataModule,
          scope: this.blueprintsScope
        });
      },
      categories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesDataModule,
          scope: this.categoriesScope
        });
      },
      configurationsScope(state: IState, getters) {
        return getters[`data/${this.configurationsDataModule}/scope`];
      },
      configurations(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.configurationsDataModule,
          scope: this.configurationsScope
        });
      }
    })
  }
})
export default class ContractProductsFiltersMixins extends Mixins(
  RequestPromises
) {
  public blueprintsScope!: string;
  public blueprints!: IBlueprint[];

  public categoriesScope = "$provision_categories_filters";
  public categories!: IProvisionCategory[];

  public configurationsScope!: string;
  public configurations!: IProvisionConfiguration[];

  public provisionDataModule: DataModules = DataModules.PROVISIONING;
  public blueprintsDataModule: DataModules =
    DataModules.PROVISIONING_BLUEPRINTS;
  public configurationsDataModule: DataModules =
    DataModules.PROVISIONING_CONFIGURATIONS;
  public categoriesDataModule: DataModules =
    DataModules.PROVISIONING_CATEGORIES;

  public filters = [] as IFilter[];

  public getOrgProvisioning(ignoreStored = false) {
    return this.$store.dispatch("org/getConfig", {
      ignoreStored,
      keys: [OrgConfigKeys.PRODUCT_PROVISIONING_ENABLED]
    });
  }

  public getFiltersData(ignoreStored = false, withStagedImports = false) {
    return Promise.all([
      this.getBlueprints(ignoreStored, withStagedImports),
      this.getCategoriesWithProviders(ignoreStored, withStagedImports),
      this.getConfigurations(ignoreStored, withStagedImports)
    ]);
  }

  public getBlueprints(ignoreStored = false, withStagedImports = false) {
    return this.$store.dispatch(`data/${this.blueprintsDataModule}/list`, {
      scope: this.blueprintsScope,
      vm: this,
      ignoreStored,
      params: {
        ...(withStagedImports ? { with_staged_imports: 1 } : {})
      }
    });
  }

  public getCategoriesWithProviders(
    ignoreStored = false,
    withStagedImports = false
  ) {
    return this.$store.dispatch(`data/${this.categoriesDataModule}/list`, {
      scope: this.categoriesScope,
      vm: this,
      params: {
        with: "providers",
        ...(withStagedImports ? { with_staged_imports: 1 } : {})
      },
      ignoreStored
    });
  }

  public createProvisionFilters(isOneTimePurchase: boolean) {
    if (!this.hasProvisioning) {
      return [];
    }

    return this.$_.map(
      this.$_.cloneDeep(CProdProvisionFilters()),
      (filter: IFilter) => {
        if (isOneTimePurchase) {
          filter.group = "purchase";
        }

        if (filter.key === "product.provision_blueprint_id") {
          filter.options = this.flatOptions(this.blueprints);
          filter.value = [this.$_.get(filter, "options[0].value", [])];
        }

        if (filter.key === "provision_provider_id") {
          filter.options = this.flatOptions(this.categories, true);
          filter.value = this.$_.get(filter, "options[0].value", []);
        }

        if (filter.key === "provision_configuration_id") {
          filter.options = this.flatOptions(this.configurations);
          filter.value = [this.$_.get(filter, "options[0].value", [])];
        }

        return filter;
      }
    );
  }

  public flatOptions(options, hasProviders = false) {
    return this.$_.map(options, option => {
      return this.$_.merge(
        {
          value: option.id.toString(),
          label: option.name,
          name: option.name
        },
        hasProviders ? { options: this.flatOptions(option.providers) } : {}
      );
    });
  }

  public getConfigurations(ignoreStored = false, withStagedImports = false) {
    return this.$store.dispatch(`data/${this.configurationsDataModule}/list`, {
      scope: this.configurationsScope,
      vm: this,
      params: {
        limit: 0,
        ...(withStagedImports ? { with_staged_imports: 1 } : {})
      },
      ignoreStored
    });
  }

  public get hasProvisioning() {
    return this.$store.getters["org/hasProvisioningEnabled"];
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.blueprintsDataModule,
      scope: this.blueprintsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: this.categoriesDataModule,
      scope: this.categoriesScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: this.configurationsDataModule,
      scope: this.configurationsScope,
      vm: this
    });
  }
}
